import * as React from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import type { ChatStatus, FileUIPart } from "ai";
import { ArrowUp, File, FileSpreadsheet, FileText, Paperclip, Square, X } from "lucide-react";
import { Button } from "../primitives/button.js";
import { cn } from "../lib/cn.js";

export interface PromptInputMessage {
  text: string;
  files: FileUIPart[];
}

export interface PromptInputProps extends Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  "onSubmit"
> {
  message: PromptInputMessage;
  onSubmit: (
    message: PromptInputMessage,
    event: React.FormEvent<HTMLFormElement>,
  ) => void | Promise<void>;
}

/**
 * Tiptap-based PromptInput with AI SDK file parts.
 *
 * This keeps the AI Elements form contract while using a rich editor instead
 * of the registry textarea, because Flint's chat composer needs multi-line
 * editing and multimodal attachments.
 */
export function PromptInput({
  className,
  message,
  onSubmit,
  children,
  ...props
}: PromptInputProps) {
  return (
    <form
      className={cn("rounded-md border border-line bg-paper shadow-card", className)}
      onSubmit={(event) => onSubmit(message, event)}
      {...props}
    >
      {children}
    </form>
  );
}

export interface PromptInputEditorProps {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Called when the user pastes one or more files into the editor. */
  onFilePaste?: (files: File[]) => void;
}

export function PromptInputEditor({
  value,
  onValueChange,
  onSubmit,
  disabled,
  placeholder = "Ask Flint...",
  className,
  onFilePaste,
}: PromptInputEditorProps) {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, horizontalRule: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    onUpdate: ({ editor: instance }) => onValueChange(instance.getText()),
    editorProps: {
      attributes: {
        class:
          "min-h-[40px] max-h-[180px] overflow-y-auto py-2 text-base leading-6 outline-none prose-none",
      },
      handleKeyDown: (_view, event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          onSubmit();
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        if (!onFilePaste) return false;
        const items = Array.from(event.clipboardData?.items ?? []);
        const fileItems = items.filter((item) => item.kind === "file");
        if (fileItems.length === 0) return false;
        const files = fileItems
          .map((item) => item.getAsFile())
          .filter((f): f is File => f !== null);
        if (files.length === 0) return false;
        onFilePaste(files);
        return true;
      },
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  React.useEffect(() => {
    if (!editor) return;
    if (value === editor.getText()) return;
    editor.commands.setContent(value);
  }, [editor, value]);

  return <EditorContent className={className} editor={editor} />;
}

export interface PromptInputAttachmentsProps {
  files: FileUIPart[];
  onRemove: (file: FileUIPart) => void;
  className?: string;
}

export function PromptInputAttachments({
  files,
  onRemove,
  className,
}: PromptInputAttachmentsProps) {
  if (files.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap gap-3 px-3 pb-1 pt-3", className)}>
      {files.map((file) => {
        const isImage = file.mediaType?.startsWith("image/") ?? false;
        const typeInfo = getFileTypeInfo(file);
        return (
          <div key={`${file.filename ?? file.mediaType}:${file.url}`} className="relative">
            <div
              className={cn(
                "h-16 w-16 overflow-hidden rounded-lg border border-line",
                isImage ? "" : typeInfo.bgColor,
              )}
            >
              {isImage ? (
                <img
                  src={file.url}
                  alt={file.filename ?? "image"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-1">
                  <typeInfo.Icon className={cn("h-7 w-7", typeInfo.iconColor)} aria-hidden />
                  <span className={cn("text-xs font-bold tracking-wide", typeInfo.iconColor)}>
                    {typeInfo.label}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full border border-line bg-paper shadow-sm text-ink-2 hover:bg-bg hover:text-ink"
              onClick={() => onRemove(file)}
              aria-label={`Remove ${file.filename ?? "attachment"}`}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function getFileTypeInfo(file: FileUIPart): {
  Icon: React.ElementType;
  iconColor: string;
  bgColor: string;
  label: string;
} {
  const ext = (file.filename?.split(".").pop() ?? "").toLowerCase();
  const mime = file.mediaType ?? "";

  if (ext === "pdf" || mime === "application/pdf") {
    return { Icon: FileText, iconColor: "text-red-500", bgColor: "bg-red-50", label: "PDF" };
  }
  if (ext === "xlsx" || mime.includes("spreadsheetml")) {
    return { Icon: FileSpreadsheet, iconColor: "text-emerald-600", bgColor: "bg-emerald-50", label: "XLSX" };
  }
  if (ext === "xls" || mime.includes("ms-excel")) {
    return { Icon: FileSpreadsheet, iconColor: "text-emerald-600", bgColor: "bg-emerald-50", label: "XLS" };
  }
  if (ext === "csv" || mime === "text/csv") {
    return { Icon: FileSpreadsheet, iconColor: "text-emerald-600", bgColor: "bg-emerald-50", label: "CSV" };
  }
  if (ext === "txt" || mime === "text/plain") {
    return { Icon: FileText, iconColor: "text-sky-500", bgColor: "bg-sky-50", label: "TXT" };
  }
  const fallbackLabel = ext.toUpperCase() || "FILE";
  return { Icon: File, iconColor: "text-ink-2", bgColor: "bg-bg", label: fallbackLabel };
}

export type PromptInputToolbarProps = React.HTMLAttributes<HTMLDivElement>;

export function PromptInputToolbar({ className, ...props }: PromptInputToolbarProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 border-t border-line px-2 py-2",
        className,
      )}
      {...props}
    />
  );
}

export interface PromptInputAttachmentButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function PromptInputAttachmentButton({
  inputRef,
  className,
  ...props
}: PromptInputAttachmentButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "grid h-9 w-9 place-items-center rounded-md border border-line text-ink-2 hover:bg-bg",
        className,
      )}
      onClick={() => inputRef.current?.click()}
      aria-label="Attach files"
      {...props}
    >
      <Paperclip className="h-4 w-4" aria-hidden />
    </button>
  );
}

export interface PromptInputSubmitProps extends React.ComponentProps<typeof Button> {
  status?: ChatStatus;
  onStop?: () => void;
}

export function PromptInputSubmit({
  status,
  onStop,
  className,
  children,
  ...props
}: PromptInputSubmitProps) {
  const busy = status === "submitted" || status === "streaming";
  return (
    <Button
      className={cn("h-9 w-9 p-0", className)}
      onClick={(event) => {
        if (busy && onStop) {
          event.preventDefault();
          onStop();
        }
      }}
      type={busy && onStop ? "button" : "submit"}
      {...props}
    >
      {children ??
        (busy ? (
          <Square className="h-4 w-4" aria-hidden />
        ) : (
          <ArrowUp className="h-4 w-4" aria-hidden />
        ))}
      <span className="sr-only">{busy ? "Stop response" : "Send message"}</span>
    </Button>
  );
}

export async function filesToFileUIParts(files: File[]): Promise<FileUIPart[]> {
  return Promise.all(
    files.map(async (file) => ({
      type: "file" as const,
      filename: file.name,
      mediaType: file.type || "application/octet-stream",
      url: await readAsDataUrl(file),
    })),
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}
